import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

import { getPsaContactsByCompanyId } from 'services/pod';
import { getTeammates } from 'services/callScribe/conference';
import { ICallScribeCallListItem, ICallScribeTeammate } from 'services/callScribe/types';
import type { IDialSuggestion } from 'services/callScribe/dialSuggestions';
import { useDialSuggestions } from 'components/CallScribe/Home/useDialSuggestions';
import { digitsOf, personMatches } from 'components/CallScribe/personMatch';
import { otherPartyPhone, relativeTimeLabel } from 'components/CallScribe/Recents/recentsHelpers';

export type TPersonKind = 'teammate' | 'ticket' | 'contact' | 'recent';
export type TPersonSource = 'team' | 'ticket' | 'company' | 'psa' | 'history' | 'call';
export type TPeopleGroupKey = 'teammates' | 'ticket' | 'contacts' | 'recent';
export type TPeopleMode = 'add' | 'dial';

export interface IPerson {
  key: string;
  kind: TPersonKind;
  source: TPersonSource;
  name: string;
  fieldName?: string;
  phone: string;
  detail?: string;
  accountId?: number;
  contactExternalId?: string | null;
  companyExternalId?: string | null;
  crmSourceId?: number | null;
}

export interface IPeopleGroup {
  key: TPeopleGroupKey;
  label: string;
  people: IPerson[];
}

export interface IPeopleDirectoryArgs {
  open: boolean;
  mode: TPeopleMode;
  crmSourceId?: number | null;
  suggestCrmSourceId?: number | null;
  companyId?: string | number | null;
  ticketPeople?: IPerson[];
  recentCalls?: ICallScribeCallListItem[];
  country: CountryCode;
  national: string;
  nameQuery: string;
}

export interface IPeopleDirectory {
  groups: IPeopleGroup[];
  loading: boolean;
  anyoneDialable: boolean;
  failed: string[];
  retry: () => void;
}

const MAX_RECENTS = 5;
const SEARCH_DEBOUNCE_MS = 250;

const GROUP_LABELS: Record<TPeopleGroupKey, string> = {
  teammates: 'Teammates',
  ticket: 'On this ticket',
  contacts: 'Contacts',
  recent: 'Recent numbers',
};

export const calledWhen = (label: string): string => {
  if (!label) return 'Recent number';
  if (label === 'Just now' || label === 'Yesterday') return `Called ${label.toLowerCase()}`;
  if (/\bago$/.test(label)) return `Called ${label}`;
  return `Called on ${label}`;
};

const ORDER: Record<TPeopleMode, TPeopleGroupKey[]> = {
  add: ['teammates', 'contacts', 'recent'],
  dial: ['ticket', 'contacts', 'recent'],
};

const hasPhone = (p: IPerson): boolean => p.kind === 'teammate' || p.phone.trim().length > 0;

const phoneKey = (phone: string, country: CountryCode): string =>
  parsePhoneNumberFromString(phone, country)?.number ?? digitsOf(phone);

const contactRows = (
  rows: Array<{ value: unknown; label: string; info?: string | null }>,
  crmSourceId: number,
): IPerson[] =>
  rows.map(r => ({
    key: `contact-${String(r.value)}`,
    kind: 'contact',
    source: 'company',
    name: r.label,
    phone: (r.info ?? '').trim(),
    contactExternalId: String(r.value),
    crmSourceId,
  }));

type TPsaIds = Pick<IPerson, 'contactExternalId' | 'companyExternalId' | 'crmSourceId'>;

const NO_PSA_IDS: TPsaIds = { contactExternalId: null, companyExternalId: null, crmSourceId: null };

export const idsOnThisPsa = (s: IDialSuggestion, psaId: number | null | undefined): TPsaIds =>
  typeof psaId === 'number' && s.crmSourceId === psaId
    ? { contactExternalId: s.contactExternalId ?? null, companyExternalId: s.companyExternalId ?? null, crmSourceId: psaId }
    : NO_PSA_IDS;

export const usePeopleDirectory = (args: IPeopleDirectoryArgs): IPeopleDirectory => {
  const { open, mode, crmSourceId, companyId, ticketPeople, recentCalls, country, national, nameQuery } = args;
  const suggestSource = args.suggestCrmSourceId === undefined ? crmSourceId : args.suggestCrmSourceId;

  const [teammates, setTeammates] = useState<ICallScribeTeammate[] | undefined>(undefined);
  const [teammatesFailed, setTeammatesFailed] = useState(false);
  const [baseContacts, setBaseContacts] = useState<IPerson[] | undefined>(undefined);
  const [searchContacts, setSearchContacts] = useState<IPerson[]>([]);
  const [contactsFailed, setContactsFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const searchRef = useRef(0);

  const companyKey = companyId == null ? '' : String(companyId).trim();
  const canSearchContacts = typeof crmSourceId === 'number' && companyKey !== '';

  useEffect(() => {
    if (!open || mode !== 'add') return undefined;
    let cancelled = false;
    setTeammatesFailed(false);
    void getTeammates()
      .then(res => {
        if (cancelled) return;
        if (res && Array.isArray(res.data)) setTeammates(res.data);
        else setTeammatesFailed(true);
      })
      .catch(() => { if (!cancelled) setTeammatesFailed(true); });
    return () => { cancelled = true; };
  }, [open, mode, attempt]);

  useEffect(() => {
    if (!open || !canSearchContacts) return undefined;
    let cancelled = false;
    setContactsFailed(false);
    void getPsaContactsByCompanyId(crmSourceId as number, companyKey, 0, 50, undefined, true)
      .then(res => {
        if (cancelled) return;
        if (res && Array.isArray(res.data)) setBaseContacts(contactRows(res.data, crmSourceId as number));
        else setContactsFailed(true);
      })
      .catch(() => { if (!cancelled) setContactsFailed(true); });
    return () => { cancelled = true; };
  }, [open, canSearchContacts, crmSourceId, companyKey, attempt]);

  const term = nameQuery.trim();
  useEffect(() => {
    const reqId = ++searchRef.current;
    if (!open || !canSearchContacts || term.length === 0) {
      setSearchContacts([]);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void getPsaContactsByCompanyId(crmSourceId as number, companyKey, 0, 50, term, true)
        .then(res => {
          if (reqId !== searchRef.current) return;
          setSearchContacts(res && Array.isArray(res.data) ? contactRows(res.data, crmSourceId as number) : []);
        })
        .catch(() => undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => { window.clearTimeout(timer); };
  }, [open, canSearchContacts, crmSourceId, companyKey, term]);

  const suggestions = useDialSuggestions(
    open && typeof suggestSource === 'number' ? suggestSource : null,
    country,
    nameQuery ? '' : national,
  );

  const query = nameQuery || national;

  const all = useMemo(() => {
    const team: IPerson[] = (teammates ?? [])
      .filter(t => t.hasPhone)
      .map(t => ({
        key: `teammate-${t.accountId}`,
        kind: 'teammate',
        source: 'team',
        name: t.name,
        phone: '',
        detail: 'Teammate',
        accountId: t.accountId,
      }));

    const ticket = (ticketPeople ?? []).filter(hasPhone);
    const keyOf = (phone: string): string => phoneKey(phone, country);
    const seenDigits = new Set(ticket.map(p => keyOf(p.phone)).filter(Boolean));

    const contacts: IPerson[] = [];
    const addContact = (p: IPerson): void => {
      if (!hasPhone(p)) return;
      const digits = keyOf(p.phone);
      if (seenDigits.has(digits)) return;
      seenDigits.add(digits);
      contacts.push(p);
    };
    [...(baseContacts ?? []), ...searchContacts].forEach(addContact);
    suggestions
      .filter(s => s.source === 'psa')
      .map((s): IPerson => ({
        key: `psa-${s.phoneE164}`,
        kind: 'contact',
        source: 'psa',
        name: s.name,
        phone: s.phoneE164,
        ...idsOnThisPsa(s, suggestSource),
      }))
      .forEach(addContact);

    const recent: IPerson[] = [];
    const addRecent = (p: IPerson): void => {
      const digits = keyOf(p.phone);
      if (!digits || seenDigits.has(digits)) return;
      seenDigits.add(digits);
      recent.push(p);
    };
    suggestions
      .filter(s => s.source === 'history')
      .map((s): IPerson => ({
        key: `history-${s.phoneE164}`,
        kind: 'recent',
        source: 'history',
        name: s.name,
        phone: s.phoneE164,
        ...idsOnThisPsa(s, suggestSource),
      }))
      .forEach(addRecent);
    (recentCalls ?? []).forEach(call => {
      const phone = otherPartyPhone(call);
      if (!phone) return;
      addRecent({
        key: `call-${digitsOf(phone)}`,
        kind: 'recent',
        source: 'call',
        name: '',
        phone,
        detail: calledWhen(relativeTimeLabel(call.startedAtUtc)),
      });
    });

    return { teammates: team, ticket, contacts, recent: recent.slice(0, MAX_RECENTS) };
  }, [teammates, ticketPeople, baseContacts, searchContacts, suggestions, suggestSource, recentCalls, country]);

  const groups = useMemo(
    () => ORDER[mode]
      .map(key => ({
        key,
        label: GROUP_LABELS[key],
        people: all[key].filter(p => personMatches(p, query)),
      }))
      .filter(g => g.people.length > 0),
    [mode, all, query],
  );

  const anyoneDialable = ORDER[mode].some(key => all[key].length > 0);

  const loading =
    (mode === 'add' && teammates === undefined && !teammatesFailed) ||
    (canSearchContacts && baseContacts === undefined && !contactsFailed);

  const failed = [
    ...(mode === 'add' && teammatesFailed ? ['Teammates'] : []),
    ...(contactsFailed ? ['Contacts'] : []),
  ];

  const retry = useCallback(() => { setAttempt(n => n + 1); }, []);

  return { groups, loading, anyoneDialable, failed, retry };
};
